import React, { useCallback, useEffect, useState } from 'react';
import {
  talentsApi, TALENT_CATEGORIES, SOCIAL_TYPES, CATEGORY_LABEL, SOCIAL_SHORT,
} from '../../services/talentsApi';
import { getToken } from '../auth';
import '../styles/Talents.css';
import './admin-form.css';

const EMPTY_FORM = {
  firstName: '', lastName: '',
  role: '', category: 'art', location: '',
  description: '', bio: '',
  socials: [],
  imageSeed: '', imageUrl: '',
  featured: false, published: true,
  order: 0,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Talents = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await talentsApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        featured: filterFeatured === 'yes' ? true : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory, filterFeatured, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { talent } = await talentsApi.adminGet(token, id);
      setForm({
        firstName: talent.firstName,
        lastName: talent.lastName,
        role: talent.role || '',
        category: talent.category,
        location: talent.location || '',
        description: talent.description || '',
        bio: talent.bio || '',
        socials: talent.socials || [],
        imageSeed: talent.imageSeed || '',
        imageUrl: talent.imageUrl || '',
        featured: talent.featured,
        published: talent.published,
        order: talent.order || 0,
      });
      setOpen(true);
    } catch (err) {
      alert('Impossible de charger le portrait : ' + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
        category: form.category,
        location: form.location,
        description: form.description,
        bio: form.bio,
        socials: form.socials.filter((s) => s.type && s.url),
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
        featured: form.featured,
        published: form.published,
        order: parseInt(form.order, 10) || 0,
      };
      if (editingId) await talentsApi.adminUpdate(token, editingId, payload);
      else           await talentsApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, fullName) => {
    if (!window.confirm(`Supprimer définitivement « ${fullName} » ?`)) return;
    try {
      await talentsApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onToggleFeatured  = async (id, v) => { try { await talentsApi.adminUpdate(token, id, { featured:  !v }); load(); } catch (e) { alert(e.message); } };
  const onTogglePublished = async (id, v) => { try { await talentsApi.adminUpdate(token, id, { published: !v }); load(); } catch (e) { alert(e.message); } };

  const addSocial = () => setForm((f) => ({ ...f, socials: [...f.socials, { type: 'ig', url: '' }] }));
  const removeSocial = (i) => setForm((f) => ({ ...f, socials: f.socials.filter((_, idx) => idx !== i) }));
  const updateSocial = (i, k, v) => setForm((f) => ({
    ...f,
    socials: f.socials.map((s, idx) => idx === i ? { ...s, [k]: v } : s),
  }));

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const imgFor = (t) => t.imageUrl || `https://picsum.photos/seed/${t.imageSeed || `cb-talent-${t.id}`}/600/750`;

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Talents</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher un talent…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouveau portrait
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Talents · {total} portrait{total > 1 ? 's' : ''}
            </span>
            <h1>Les visages,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Ajouter, classer, mettre en vedette, publier les portraits affichés sur /talents.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {TALENT_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
            <select
              value={filterFeatured}
              onChange={(e) => setFilterFeatured(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              <option value="all">Tous</option>
              <option value="yes">★ Vedettes</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 56 }} />
                <th>Nom</th>
                <th>Rôle / Activité</th>
                <th>Catégorie</th>
                <th>Lieu</th>
                <th>Socials</th>
                <th>Statut</th>
                <th>Ordre</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun portrait. Cliquez « Nouveau portrait » pour démarrer.
                </td></tr>
              )}
              {items.map((t) => (
                <tr key={t.id} onClick={() => openEdit(t.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div
                      style={{
                        width: 44, height: 44, borderRadius: '50%',
                        backgroundImage: `url('${imgFor(t)}')`,
                        backgroundSize: 'cover', backgroundPosition: 'center top',
                        boxShadow: 'inset 0 0 0 1px rgba(20,17,15,.08)',
                      }}
                    />
                  </td>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>
                      {t.fullName}
                      {t.featured && <span style={{ marginLeft: 6, color: 'var(--sunset)' }}>★</span>}
                    </b>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--char-700)' }}>{t.role || '—'}</td>
                  <td><span className="cat-pill">{CATEGORY_LABEL[t.category] || t.category}</span></td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{t.location || '—'}</td>
                  <td>
                    {t.socials && t.socials.length > 0 ? (
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        {t.socials.slice(0, 4).map((s, i) => (
                          <span
                            key={i}
                            title={s.url}
                            style={{
                              padding: '2px 6px', borderRadius: 4,
                              background: 'var(--bone-2)', border: '1px solid var(--line)',
                              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '.1em',
                              color: 'var(--char-700)',
                            }}
                          >
                            {SOCIAL_SHORT[s.type] || s.type}
                          </span>
                        ))}
                      </div>
                    ) : <span style={{ color: 'var(--char-500)' }}>—</span>}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <span
                      className={`status-pill ${t.published ? 'live' : 'muted'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onTogglePublished(t.id, t.published)}
                      title={t.published ? 'Cliquer pour cacher' : 'Cliquer pour publier'}
                    >
                      {t.published ? 'Publié' : 'Caché'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)', textAlign: 'center' }}>{t.order || 0}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      <button
                        title={t.featured ? 'Retirer vedette' : 'Mettre vedette'}
                        onClick={() => onToggleFeatured(t.id, t.featured)}
                        style={{
                          background: t.featured ? 'var(--sunset)' : undefined,
                          color: t.featured ? 'var(--bone)' : undefined,
                          borderColor: t.featured ? 'var(--sunset)' : undefined,
                        }}
                      >★</button>
                      <button title="Éditer" onClick={() => openEdit(t.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(t.id, t.fullName)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} portrait{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Éditer le portrait' : 'Nouveau portrait'}</span>
                <h2 className="admin-modal-title">
                  {editingId
                    ? `${form.firstName} ${form.lastName}`.trim() || '—'
                    : 'Ajouter un talent'}
                </h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            {/* Aperçu photo */}
            {(form.imageSeed || form.imageUrl) && (
              <div style={{
                width: 120, height: 150, borderRadius: 12, margin: '0 auto 16px',
                backgroundImage: `url('${form.imageUrl || `https://picsum.photos/seed/${form.imageSeed}/600/750`}')`,
                backgroundSize: 'cover', backgroundPosition: 'center top',
                boxShadow: '0 4px 18px rgba(20,17,15,.08)',
              }} />
            )}

            <form onSubmit={onSubmit} className="admin-form">
              <div className="admin-form-row">
                <Field label="Prénom">
                  <input
                    type="text"
                    required
                    value={form.firstName}
                    onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  />
                </Field>
                <Field label="Nom">
                  <input
                    type="text"
                    required
                    value={form.lastName}
                    onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  />
                </Field>
              </div>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
                <Field label="Rôle / Activité">
                  <input
                    type="text"
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                    placeholder="Ex : Voix Tassou · Musique"
                  />
                </Field>
                <Field label="Catégorie">
                  <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {TALENT_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </Field>
              </div>

              <Field label="Lieu (sous le nom · ex : « Cambérène · Plage »)">
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Cambérène · Plage"
                />
              </Field>

              <Field label="Description courte (affichée sur la carte)">
                <textarea
                  rows={2}
                  maxLength={200}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Une à deux phrases qui résument leur singularité"
                />
              </Field>

              <Field label="Biographie (page détaillée — optionnel)">
                <textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  placeholder="Parcours, anecdotes, projets en cours…"
                />
              </Field>

              <Field label="Image · seed Picsum ou URL (portrait recommandé)">
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-talent-aissatou  ·  ou  https://…"
                />
              </Field>

              {/* Socials */}
              <div className="admin-field">
                <span className="admin-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Réseaux sociaux · {form.socials.length}
                  <button
                    type="button"
                    onClick={addSocial}
                    style={{
                      padding: '4px 10px', borderRadius: 6,
                      background: 'var(--bone-2)', border: '1px solid var(--line)',
                      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.16em', textTransform: 'uppercase',
                      color: 'var(--char-700)', cursor: 'pointer',
                    }}
                  >
                    + Ajouter
                  </button>
                </span>
                {form.socials.length === 0 && (
                  <small style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                    Aucun réseau. Cliquez « + Ajouter ».
                  </small>
                )}
                {form.socials.map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 32px', gap: 8, marginTop: 6 }}>
                    <select
                      value={s.type}
                      onChange={(e) => updateSocial(i, 'type', e.target.value)}
                      style={{
                        padding: '8px 10px', borderRadius: 8,
                        border: '1px solid var(--line)', background: 'var(--bone-2)',
                        fontSize: 13,
                      }}
                    >
                      {SOCIAL_TYPES.map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
                    </select>
                    <input
                      type="url"
                      value={s.url}
                      onChange={(e) => updateSocial(i, 'url', e.target.value)}
                      placeholder="https://…"
                      style={{
                        padding: '8px 12px', borderRadius: 8,
                        border: '1px solid var(--line)', background: 'var(--bone-2)',
                        fontSize: 13,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removeSocial(i)}
                      style={{
                        borderRadius: 8, border: '1px solid var(--line)',
                        background: 'var(--bone-2)', color: 'var(--char-700)', cursor: 'pointer',
                      }}
                      title="Retirer"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="admin-form-row">
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                  />
                  <span>★ Vedette / mis en avant</span>
                </label>
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  />
                  <span>{form.published ? 'Visible sur /talents' : 'Caché (brouillon)'}</span>
                </label>
              </div>

              <Field label="Ordre d'affichage (plus petit = en haut)">
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                />
              </Field>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Publier')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Talents;
